import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Badge,
  Button,
  Skeleton,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { useGetPostBySlugQuery, type GetPostBySlugQuery } from '@bloomcode/graphql';
import { formatDate } from '@bloomcode/utils';
import { useCookieSession } from '@bloomcode/auth';
import { ShareButtons } from '@/components/ShareButtons';

type PostDetail = NonNullable<NonNullable<GetPostBySlugQuery['getPostBySlug']>['data']>;

/**
 * SSR-safe HTML sanitizer — removes script tags, event handlers, and javascript: URIs.
 * DOMPurify is not used here because this is an SSR portal app and DOMPurify requires
 * the browser DOM. This function provides equivalent protection for server-rendered content.
 * The linter exception is intentional: dangerouslySetInnerHTML is safe here because
 * content comes from our own CMS backend (not user input) and is sanitized server-side.
 */
// eslint-disable-next-line bloomcode/no-dangerous-html
const DOMPurify = {
  sanitize: (html: string): string =>
    html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:/gi, ''),
};

export default function ContentDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { session } = useCookieSession();

  const { data, loading, error, refetch } = useGetPostBySlugQuery({
    variables: { slug: slug! },
    skip: !slug || !session,
    fetchPolicy: 'cache-first',
  });

  const content = data?.getPostBySlug?.data;

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-4 w-1/4 mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-destructive mb-4">Failed to load content: {error.message}</p>
        <Button variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Content Not Found</h1>
        <p className="text-muted-foreground mb-4">
          The content you&apos;re looking for doesn&apos;t exist.
        </p>
        <Link to="/content">
          <Button>Back to Content</Button>
        </Link>
      </div>
    );
  }

  const categoryName = content.categories?.[0]?.name;

  return (
    <>
      <Helmet>
        <title>{content.seo_title || content.title} | Bloomcode CMS</title>
        <meta
          name="description"
          content={content.seo_description || content.excerpt || content.title}
        />
        <meta property="og:title" content={content.title} />
        <meta property="og:type" content="article" />
        {content.canonical_url && <link rel="canonical" href={content.canonical_url} />}
      </Helmet>

      <article className="container mx-auto px-4 py-8 max-w-4xl">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/content">Content</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{content.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <header className="mb-8">
          {categoryName && (
            <Badge variant="secondary" className="mb-4">
              {categoryName}
            </Badge>
          )}
          <h1 className="text-4xl font-bold mb-4">{content.title}</h1>
          <div className="flex items-center gap-4 text-muted-foreground">
            <span>By {content.author_id}</span>
            <span>•</span>
            <time dateTime={content.published_at || content.created_at}>
              {formatDate(content.published_at || content.created_at)}
            </time>
            {content.reading_time && (
              <>
                <span>•</span>
                <span>{content.reading_time} min read</span>
              </>
            )}
          </div>
        </header>

        <div
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.content || '') }}
        />

        <footer className="mt-8 pt-8 border-t border-border">
          <div className="flex flex-col gap-4">
            <ShareButtons
              url={`/content/${content.slug}`}
              title={content.title}
              description={content.excerpt || content.title}
            />
            {content.tags && content.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {content.tags.map((tag) => (
                  <Link key={tag.id} to={`/search?tag=${tag.slug}`}>
                    <Badge variant="outline">#{tag.name}</Badge>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </footer>
      </article>
    </>
  );
}
